import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Activity, Shield, MessageSquare, Sparkles, BookOpen, Wallet, TrendingUp, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SystemHealthTile — one-glance CEO morning check. Rolls up SiteVault,
 * WhatsApp sends today, Operator Mira writes this week, stale SOPs,
 * overdue receivables, and open TDB cost anomalies. Auto-refreshes every 60s.
 *
 * CEO-only. Backend returns 403 for anyone else; we hide the tile on 403 so
 * sub-leaders don't see a broken panel.
 */
const DOT = { ok: '#2D8B5E', warn: '#C98A1F', fail: '#B91C1C', running: '#2A7B88', unknown: '#9CA3AF', error: '#9CA3AF' };
const BG = { ok: '#ECFDF5', warn: '#FEF6E5', fail: '#FEE2E2', running: '#F0FAFB', unknown: '#F7F5F2', error: '#F7F5F2' };
const LABEL = { ok: 'Healthy', warn: 'Watch', fail: 'Needs action', running: 'Running', unknown: 'Unknown', error: 'Unknown' };

const fmtINR = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
};

export default function SystemHealthTile({ className = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/system-health');
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) setHidden(true);
      // silently retry on other errors — the tile is non-critical
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  if (hidden) return null;
  if (loading) {
    return (
      <div className={`rounded-lg border px-4 py-3 ${className}`} style={{ borderColor: '#E5E1DC' }}
           data-testid="system-health-loading">
        <div className="flex items-center gap-2 text-xs" style={{ color: '#9CA3AF' }}>
          <Activity size={14} /> Loading system health…
        </div>
      </div>
    );
  }
  if (!data) return null;

  const s = data.signals || {};
  const overall = data.overall || 'unknown';

  const cells = [
    {
      key: 'sitevault', icon: <Shield size={14} />, label: 'Backup', status: s.sitevault?.status || 'unknown',
      headline: s.sitevault?.status === 'ok'
        ? `${s.sitevault.age_hours ?? '—'}h ago`
        : (s.sitevault?.status === 'fail' ? 'Failed' : (LABEL[s.sitevault?.status] || '—')),
      sub: `next ${s.sitevault?.next_daily_ist || '04:00 IST'}`,
    },
    {
      key: 'whatsapp', icon: <MessageSquare size={14} />, label: 'WhatsApp today', status: 'ok',
      headline: `${s.whatsapp?.sent_today ?? 0}`,
      sub: s.whatsapp?.breakdown
        ? `radio ${s.whatsapp.breakdown.radio || 0} · mira ${s.whatsapp.breakdown.mira || 0}`
        : '',
    },
    {
      key: 'mira', icon: <Sparkles size={14} />, label: 'Mira writes 7d', status: 'ok',
      headline: `${s.mira?.writes_7d ?? 0}`,
      sub: s.mira?.undone_7d ? `${s.mira.undone_7d} undone` : 'no undos',
    },
    {
      key: 'sops', icon: <BookOpen size={14} />, label: 'Stale SOPs', status: s.sops?.status || 'ok',
      headline: `${s.sops?.stale_sop_documents ?? s.sops?.stale_count ?? 0}`,
      sub: `${s.sops?.stale_assignments_count ?? 0} acks across ${s.sops?.stale_people_count ?? 0} people`,
    },
    {
      key: 'receivables', icon: <Wallet size={14} />, label: 'Overdue AR', status: s.receivables?.status || 'ok',
      headline: `${s.receivables?.overdue_count ?? 0}`,
      sub: fmtINR(s.receivables?.overdue_amount || 0),
    },
    {
      key: 'tdb', icon: <TrendingUp size={14} />, label: 'TDB anomalies', status: s.tdb_anomalies?.status || 'ok',
      headline: `${s.tdb_anomalies?.open_alerts ?? 0}`,
      sub: s.tdb_anomalies?.top_city
        ? `${s.tdb_anomalies.top_city} +${s.tdb_anomalies.top_delta_pct}pp`
        : `≥${s.tdb_anomalies?.threshold_pct ?? 20}pp MoM`,
    },
  ];

  return (
    <div className={`rounded-lg border ${className}`}
         style={{ borderColor: '#E5E1DC', backgroundColor: BG[overall] || '#FFFFFF' }}
         data-testid="system-health-tile">
      <div className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: DOT[overall] || DOT.unknown }}
                data-testid="system-health-overall-dot" />
          <span className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: '#6B7280' }}>
            System Health
          </span>
          <span className="text-xs font-semibold" style={{ color: DOT[overall] || DOT.unknown }}
                data-testid="system-health-overall-label">
            {LABEL[overall] || '—'}
          </span>
        </div>
        <button onClick={load} className="text-[10px] flex items-center gap-1 hover:text-[#2A7B88]"
                style={{ color: '#9CA3AF' }} title="Refresh now" data-testid="system-health-refresh">
          <RefreshCw size={10} /> refresh
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x md:divide-y-0 divide-y"
           style={{ borderColor: '#F3F0EB' }}>
        {cells.map((c) => (
          <div key={c.key} className="px-4 py-2.5 flex items-start gap-2"
               style={{ borderColor: '#F3F0EB' }}
               data-testid={`system-health-${c.key}`}>
            <span style={{ color: DOT[c.status] || DOT.unknown }}>{c.icon}</span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>{c.label}</p>
              <p className="text-sm font-semibold font-mono" style={{ color: '#1B2A4A' }}>{c.headline}</p>
              {c.sub ? (
                <p className="text-[10px] truncate" style={{ color: '#6B7280' }}>{c.sub}</p>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
